import React, { useState, useMemo, useEffect, useRef } from "react";
import { Row, Col, Form, InputGroup, Table, Alert, Spinner, Badge } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { apiClient } from "../services/api";

// Mapping des codes ISO vers noms de pays
const countryNames = {
  'GA': 'Gabon',
  'CG': 'Congo',
  'CD': 'RDC (Congo)'
};

// Données de démonstration (fallback)
const demoData = [
  { id: 1, country_iso: 'GA', tier_level: 1, min_volume: 0, max_volume: 1000, currency: 'FCFA', sms_price: 45.00, created_at: '18/05/2026 12:09' },
  { id: 4, country_iso: 'GA', tier_level: 2, min_volume: 1001, max_volume: 30000, currency: 'FCFA', sms_price: 43.00, created_at: '18/05/2026 12:09' },
  { id: 7, country_iso: 'GA', tier_level: 3, min_volume: 30001, max_volume: 1000000, currency: 'FCFA', sms_price: 41.00, created_at: '18/05/2026 12:09' },
  { id: 10, country_iso: 'GA', tier_level: 4, min_volume: 1000001, max_volume: null, currency: 'FCFA', sms_price: 39.00, created_at: '18/05/2026 12:09' },
  { id: 13, country_iso: 'CG', tier_level: 1, min_volume: 0, max_volume: 1000, currency: 'FCFA', sms_price: 40.00, created_at: '18/05/2026 12:51' },
  { id: 16, country_iso: 'CG', tier_level: 2, min_volume: 1001, max_volume: 30000, currency: 'FCFA', sms_price: 36.00, created_at: '18/05/2026 12:51' },
  { id: 19, country_iso: 'CG', tier_level: 3, min_volume: 30001, max_volume: 1000000, currency: 'FCFA', sms_price: 32.00, created_at: '18/05/2026 12:51' },
  { id: 22, country_iso: 'CG', tier_level: 4, min_volume: 1000001, max_volume: null, currency: 'FCFA', sms_price: 30.00, created_at: '18/05/2026 12:51' },
  { id: 25, country_iso: 'CD', tier_level: 1, min_volume: 0, max_volume: 1000, currency: 'CDF', sms_price: 40.00, created_at: '18/05/2026 12:51' },
  { id: 28, country_iso: 'CD', tier_level: 2, min_volume: 1001, max_volume: 30000, currency: 'CDF', sms_price: 36.00, created_at: '18/05/2026 12:51' },
  { id: 31, country_iso: 'CD', tier_level: 3, min_volume: 30001, max_volume: 1000000, currency: 'CDF', sms_price: 32.00, created_at: '18/05/2026 12:51' },
  { id: 34, country_iso: 'CD', tier_level: 4, min_volume: 1000001, max_volume: null, currency: 'CDF', sms_price: 30.00, created_at: '18/05/2026 12:51' }
];

export default function PaymentHistory() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('country_iso');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedTarifs, setSelectedTarifs] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [tarifs, setTarifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countries, setCountries] = useState([]);

  const initDoneRef = useRef(false);

  // ============================================
  // HELPERS
  // ============================================
  const extractUniqueCountries = (data) => {
    const uniqueCountries = [...new Set(data.map(item => item.country_iso))];
    return uniqueCountries.map(iso => ({
      iso: iso,
      name: countryNames[iso] || iso
    }));
  };

  // ============================================
  // FETCH
  // ============================================
  const fetchTarifs = async (showLoader = false, countryFilter = null) => {
    try {
      if (showLoader) setRefreshLoading(true);
      else setLoading(true);
      setError(null);

      const countryCode = countryFilter !== undefined ? countryFilter : selectedCountry;
      const url = countryCode ? `/sms-pricing?country=${countryCode}` : '/sms-pricing';

      const response = await apiClient.get(url);
      const result = response.data;

      if (Array.isArray(result) && result.length > 0) {
        setTarifs(result);
        setCountries(extractUniqueCountries(result));
      } else {
        console.warn('⚠️ API vide, utilisation des données de démo');
        setTarifs(demoData);
        setCountries(extractUniqueCountries(demoData));
      }
    } catch (err) {
      console.error('❌ Erreur API:', err.message);
      setTarifs(demoData);
      setCountries(extractUniqueCountries(demoData));
      setError('Données de démonstration affichées (API indisponible)');
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  };

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    fetchTarifs();
  }, []);

  const handleCountryFilterChange = (countryCode) => {
    setSelectedCountry(countryCode);
    setCurrentPage(1);
    fetchTarifs(false, countryCode);
  };

  const handleRefresh = () => {
    fetchTarifs(true, selectedCountry);
  };

  // ============================================
  // FORMATAGE
  // ============================================
  const formatTarifData = (tarif) => {
    const formatVolume = (min, max) => {
      if (min === 0 && max === 1000) return '0 - 1 000';
      if (min === 1001 && max === 30000) return '1 001 - 30 000';
      if (min === 30001 && max === 1000000) return '30 001 - 1 000 000';
      if (min === 1000001 && max === null) return '1 000 001+';
      if (max === null) return `${min?.toLocaleString() || 0}+`;
      return `${min?.toLocaleString() || 0} - ${max?.toLocaleString() || '∞'}`;
    };

    const getTierLabel = (level) => `Niveau ${level}`;

    const getTierColor = (level) => {
      switch (level) {
        case 4: return 'red';
        case 3: return 'amber';
        case 2: return 'blue';
        default: return 'gray';
      }
    };

    return {
      id: tarif.id,
      country_iso: tarif.country_iso,
      country_name: countryNames[tarif.country_iso] || tarif.country_iso,
      tier_level: tarif.tier_level,
      tier_label: getTierLabel(tarif.tier_level),
      tier_color: getTierColor(tarif.tier_level),
      min_volume: tarif.min_volume,
      max_volume: tarif.max_volume,
      volume_range: formatVolume(tarif.min_volume, tarif.max_volume),
      currency: tarif.currency,
      sms_price: parseFloat(tarif.sms_price).toFixed(2),
      created_at: tarif.created_at
    };
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    if (!tarifs || tarifs.length === 0) return [];

    let formattedTarifs = tarifs.map(formatTarifData);

    let filtered = formattedTarifs.filter(item =>
      item.country_name.toLowerCase().includes(filterText.toLowerCase()) ||
      item.tier_label.toLowerCase().includes(filterText.toLowerCase()) ||
      item.volume_range.toLowerCase().includes(filterText.toLowerCase()) ||
      item.sms_price.toString().includes(filterText) ||
      item.currency.toLowerCase().includes(filterText.toLowerCase())
    );

    if (selectedCountry) {
      filtered = filtered.filter(item => item.country_iso === selectedCountry);
    }

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'sms_price') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      if (sortField === 'tier_level') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [tarifs, filterText, sortField, sortDirection, selectedCountry]);

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = tarifs.length;
    const uniqueCountries = new Set(tarifs.map(t => t.country_iso)).size;
    const prices = tarifs.map(t => parseFloat(t.sms_price)).filter(p => !isNaN(p));
    const avgPrice = prices.length > 0 ? (prices.reduce((a, b) => a + b, 0) / prices.length).toFixed(2) : 0;
    const minPrice = prices.length > 0 ? Math.min(...prices).toFixed(2) : 0;

    return { total, uniqueCountries, avgPrice, minPrice };
  }, [tarifs]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedTarifs(currentItems.map(tarif => tarif.id));
    else setSelectedTarifs([]);
  };

  const handleSelectTarif = (tarifId) => {
    setSelectedTarifs(prev =>
      prev.includes(tarifId)
        ? prev.filter(id => id !== tarifId)
        : [...prev, tarifId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedTarifs.length === currentItems.length;
  const isSomeSelected = selectedTarifs.length > 0 && selectedTarifs.length < currentItems.length;

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedTarifs.length > 0
      ? tarifs.filter(tarif => selectedTarifs.includes(tarif.id)).map(formatTarifData)
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      alert("Aucune donnée à exporter");
      setExportLoading(false);
      return;
    }

    if (format === "csv") {
      const headers = ["Pays", "Niveau", "Volume", "Prix SMS", "Devise", "Date Création"];
      const csvContent = [
        "\uFEFF" + headers.join(";"),
        ...dataToExport.map(p => [
          p.country_name, p.tier_label, p.volume_range, p.sms_price, p.currency, p.created_at
        ].map(v => `"${v}"`).join(";"))
      ].join("\n");
      downloadFile(csvContent, "text/csv;charset=utf-8;", `export-tarifs-${Date.now()}.csv`);
    } else if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(dataToExport.map(p => ({
        "Pays": p.country_name, "Niveau": p.tier_label, "Volume SMS": p.volume_range,
        "Prix SMS": p.sms_price, "Devise": p.currency, "Date Création": p.created_at
      })));
      ws['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 20 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tarifs SMS");
      XLSX.writeFile(wb, `export-tarifs-${Date.now()}.xlsx`);
    } else if (format === "json") {
      downloadFile(JSON.stringify(dataToExport, null, 2), "application/json;charset=utf-8;", `export-tarifs-${Date.now()}.json`);
    } else if (format === "pdf") {
      generatePDF(dataToExport);
    }

    setExportLoading(false);
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    const primaryColor = [41, 128, 185];
    const headerColor = [240, 240, 240];
    const alternateColor = [248, 249, 250];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 25, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("TARIFS DES SERVICES SMS", 105, 15, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
    doc.text(`Total: ${data.length} tarif(s)`, 180, 22, { align: 'right' });

    const pageWidth = 210;
    const margin = 10;
    let yPosition = 35;
    const colWidths = [20, 15, 25, 15, 12, 20];
    const headers = ["PAYS", "NIVEAU", "VOLUME", "PRIX SMS", "DEVISE", "DATE CRÉATION"];

    doc.setFillColor(...headerColor);
    doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");

    let xPosition = margin;
    headers.forEach((header, index) => {
      doc.text(header, xPosition + 2, yPosition);
      xPosition += colWidths[index];
    });
    yPosition += 6;
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");

    data.forEach((item, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        let newX = margin;
        headers.forEach((header, idx) => {
          doc.text(header, newX + 2, yPosition);
          newX += colWidths[idx];
        });
        yPosition += 6;
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
      }

      if (index % 2 === 0) {
        doc.setFillColor(...alternateColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
      }

      const rowData = [
        item.country_name, item.tier_label, item.volume_range,
        `${item.sms_price}`, item.currency, item.created_at
      ];

      let currentX = margin;
      rowData.forEach((cell, cellIndex) => {
        if (cellIndex === 3) doc.setTextColor(41, 128, 185);
        else doc.setTextColor(0, 0, 0);
        doc.text(cell, currentX + 2, yPosition);
        currentX += colWidths[cellIndex];
      });

      yPosition += 6;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 275, pageWidth - margin, 275);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
    }

    doc.save(`tarifs-sms-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="ph-sortable-header" onClick={() => handleSort(field)}>
      <span className="ph-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ph-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="ph-page main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="ph-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="ph-header-icon">
              <i className="bi bi-currency-exchange"></i>
            </div>
            <div>
              <h3 className="ph-header-title mb-1">Nos tarifs SMS</h3>
              <p className="ph-header-subtitle mb-0">
                Consultation des tarifs par pays et par volume
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <div className="ph-country-select">
              <i className="bi bi-globe2"></i>
              <select
                className="ph-select"
                value={selectedCountry}
                onChange={(e) => handleCountryFilterChange(e.target.value)}
                disabled={loading}
              >
                <option value="">🌍 Tous les pays</option>
                {countries.map((country) => (
                  <option key={country.iso} value={country.iso}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="ph-btn-secondary"
              onClick={handleRefresh}
              disabled={refreshLoading}
            >
              <i className={`bi ${refreshLoading ? 'bi-arrow-repeat ph-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>

            <div className="dropdown">
              <button
                className="ph-btn-primary"
                type="button"
                data-bs-toggle="dropdown"
                disabled={exportLoading}
              >
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat ph-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </button>
              <ul className="dropdown-menu ph-dropdown-menu">
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('csv')}>
                    <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('excel')}>
                    <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('pdf')}>
                    <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleExport('json')}>
                    <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="ph-alert ph-alert-warning mb-4">
          <i className="bi bi-info-circle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <button className="ph-btn-retry" onClick={handleRefresh}>
            <i className="bi bi-arrow-clockwise"></i>
            Réessayer
          </button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <div className="ph-stat-card ph-stat-blue">
            <div className="ph-stat-icon-wrapper ph-stat-icon-blue">
              <i className="bi bi-list-ul"></i>
            </div>
            <div className="ph-stat-content">
              <div className="ph-stat-label">Total tarifs</div>
              <div className="ph-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="ph-stat-card ph-stat-green">
            <div className="ph-stat-icon-wrapper ph-stat-icon-green">
              <i className="bi bi-globe2"></i>
            </div>
            <div className="ph-stat-content">
              <div className="ph-stat-label">Pays couverts</div>
              <div className="ph-stat-value">{stats.uniqueCountries.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="ph-stat-card ph-stat-violet">
            <div className="ph-stat-icon-wrapper ph-stat-icon-violet">
              <i className="bi bi-graph-up"></i>
            </div>
            <div className="ph-stat-content">
              <div className="ph-stat-label">Prix moyen</div>
              <div className="ph-stat-value">{stats.avgPrice}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="ph-stat-card ph-stat-amber">
            <div className="ph-stat-icon-wrapper ph-stat-icon-amber">
              <i className="bi bi-tag-fill"></i>
            </div>
            <div className="ph-stat-content">
              <div className="ph-stat-label">Meilleur prix</div>
              <div className="ph-stat-value">{stats.minPrice}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="ph-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="ph-search-wrapper">
              <i className="bi bi-search ph-search-icon"></i>
              <input
                type="text"
                className="ph-search-input"
                placeholder="Rechercher par pays, niveau, volume, prix..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="ph-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="ph-select-full"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </Col>
          <Col lg={3} md={6} className="text-md-end">
            <span className="ph-filter-info">
              <i className="bi bi-info-circle"></i>
              <strong>{filteredAndSortedItems.length}</strong> tarif{filteredAndSortedItems.length > 1 ? 's' : ''}
            </span>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="ph-table-card">
        {loading ? (
          <div className="ph-loading">
            <div className="ph-loading-spinner"></div>
            <div className="ph-loading-text">Chargement des tarifs...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="ph-table mb-0">
                <thead>
                  <tr>
                    <SortableHeader field="country_name">
                      <i className="bi bi-globe me-1"></i>Pays
                    </SortableHeader>
                    <SortableHeader field="tier_level">
                      <i className="bi bi-layers me-1"></i>Niveau
                    </SortableHeader>
                    <SortableHeader field="volume_range">
                      <i className="bi bi-bar-chart me-1"></i>Volume SMS
                    </SortableHeader>
                    <SortableHeader field="sms_price">
                      <i className="bi bi-cash-coin me-1"></i>Prix SMS
                    </SortableHeader>
                    <SortableHeader field="currency">
                      <i className="bi bi-currency-dollar me-1"></i>Devise
                    </SortableHeader>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((tarif) => (
                      <tr key={tarif.id} className="ph-row">
                        <td>
                          <span className="ph-country-badge">
                            <i className="bi bi-geo-alt-fill"></i>
                            {tarif.country_name}
                          </span>
                        </td>
                        <td>
                          <span className={`ph-tier-badge ph-tier-${tarif.tier_color}`}>
                            <i className="bi bi-layers-fill"></i>
                            {tarif.tier_label}
                          </span>
                        </td>
                        <td>
                          <span className="ph-volume">
                            <i className="bi bi-bar-chart-line"></i>
                            {tarif.volume_range}
                          </span>
                        </td>
                        <td>
                          <span className="ph-price">
                            {parseFloat(tarif.sms_price).toLocaleString('fr-FR')}
                          </span>
                        </td>
                        <td>
                          <span className="ph-currency">{tarif.currency}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">
                        <div className="ph-empty-state">
                          <div className="ph-empty-icon">
                            <i className="bi bi-currency-exchange"></i>
                          </div>
                          <h5 className="ph-empty-title">Aucun tarif trouvé</h5>
                          <p className="ph-empty-text">
                            {filterText
                              ? "Aucun tarif ne correspond à votre recherche"
                              : "Aucun tarif disponible pour le moment"}
                          </p>
                          {filterText && (
                            <button className="ph-btn-empty-reset" onClick={() => setFilterText('')}>
                              <i className="bi bi-arrow-clockwise me-2"></i>
                              Réinitialiser
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="ph-pagination">
                <div className="ph-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                </div>
                <div className="ph-pagination-controls">
                  <button
                    className="ph-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button
                    className="ph-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`ph-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    className="ph-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button
                    className="ph-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .ph-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .ph-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .ph-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .ph-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .ph-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .ph-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== COUNTRY SELECT ========== */
        .ph-country-select {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .ph-country-select > i {
          position: absolute;
          left: 14px;
          color: #64748b;
          z-index: 5;
          pointer-events: none;
        }
        .ph-select {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 10px 18px 10px 40px;
          font-size: 0.875rem;
          font-weight: 500;
          background-color: white;
          min-width: 200px;
          transition: all 0.2s;
          cursor: pointer;
          outline: none;
          font-family: inherit;
        }
        .ph-select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* ========== BUTTONS ========== */
        .ph-btn-primary, .ph-btn-secondary, .ph-btn-retry {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .ph-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .ph-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .ph-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .ph-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .ph-btn-retry {
          background: white;
          color: #92400e;
          border: 1px solid #fde68a;
          padding: 6px 14px;
          font-size: 0.8rem;
        }
        .ph-btn-retry:hover {
          background: #92400e;
          color: white;
          border-color: #92400e;
        }
        .ph-dropdown-menu {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1);
          padding: 6px;
          position: absolute;
          z-index: 1000;
        }
        .ph-dropdown-menu .dropdown-item {
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
        }
        .ph-dropdown-menu .dropdown-item:hover {
          background: #f1f5f9;
        }

        /* ========== ALERTS ========== */
        .ph-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .ph-alert > i {
          font-size: 1.3rem;
          flex-shrink: 0;
        }
        .ph-alert-warning {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border-left: 4px solid #f59e0b;
        }
        .ph-alert-warning > i {
          color: #f59e0b;
        }

        /* ========== STATS ========== */
        .ph-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .ph-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .ph-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .ph-stat-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .ph-stat-icon-green  { background: #d1fae5; color: #10b981; }
        .ph-stat-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .ph-stat-icon-amber  { background: #fef3c7; color: #f59e0b; }
        .ph-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .ph-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .ph-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== FILTRES ========== */
        .ph-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .ph-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .ph-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .ph-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .ph-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .ph-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .ph-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .ph-select-full {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          cursor: pointer;
          font-family: inherit;
        }
        .ph-select-full:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .ph-filter-info {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 11px 16px;
          background: #f1f5f9;
          border-radius: 10px;
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 500;
        }
        .ph-filter-info strong {
          color: #3b82f6;
        }

        /* ========== TABLE ========== */
        .ph-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .ph-table {
          font-size: 0.875rem;
        }
        .ph-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          border-top: none;
          white-space: nowrap;
        }
        .ph-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .ph-row {
          transition: background-color 0.15s;
        }
        .ph-row:hover {
          background-color: #f8fafc;
        }
        .ph-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .ph-sortable-header:hover {
          color: #3b82f6;
        }
        .ph-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .ph-sort-icon {
          color: #3b82f6;
          font-size: 0.75rem;
        }

        /* Badges */
        .ph-country-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1e40af;
          border: 1px solid #bfdbfe;
        }
        .ph-country-badge > i {
          font-size: 0.85rem;
        }

        .ph-tier-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          border-radius: 8px;
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .ph-tier-badge > i {
          font-size: 0.8rem;
        }
        .ph-tier-red    { background: #fee2e2; color: #991b1b; }
        .ph-tier-amber  { background: #fef3c7; color: #92400e; }
        .ph-tier-blue   { background: #dbeafe; color: #1e40af; }
        .ph-tier-gray   { background: #f1f5f9; color: #475569; }

        .ph-volume {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
          color: #475569;
          font-weight: 500;
        }
        .ph-volume > i {
          color: #94a3b8;
          font-size: 0.9rem;
        }

        .ph-price {
          display: inline-block;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          font-variant-numeric: tabular-nums;
          padding: 4px 10px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-radius: 6px;
          color: #059669;
        }

        .ph-currency {
          display: inline-block;
          padding: 3px 10px;
          background: #f1f5f9;
          color: #64748b;
          border-radius: 5px;
          font-size: 0.75rem;
          font-weight: 700;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }

        /* ========== LOADING / EMPTY ========== */
        .ph-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .ph-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: ph-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .ph-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .ph-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .ph-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .ph-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .ph-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .ph-btn-empty-reset {
          display: inline-flex;
          align-items: center;
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .ph-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== PAGINATION ========== */
        .ph-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .ph-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ph-pagination-info strong {
          color: #0f172a;
        }
        .ph-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ph-page-btn {
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .ph-page-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .ph-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .ph-page-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== ANIMATIONS ========== */
        .ph-spin {
          animation: ph-spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes ph-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .ph-header {
            padding: 20px;
          }
          .ph-header-title {
            font-size: 1.2rem;
          }
          .ph-pagination {
            flex-direction: column;
          }
          .ph-stat-value {
            font-size: 1.25rem;
          }
          .ph-select {
            min-width: 100%;
          }
        }
      `}</style>
    </div>
  );
}